import { supabase } from '../lib/supabase'

/**
 * Dispatches — the studio's open email threads (25 Sep 2026).
 *
 * Tom: "the problem I have is organisation, and getting a clear picture of
 * where we are across numerous preliminary chats."
 *
 * Everything on this screen is composed from four facts about the mail, and
 * not one of them is a judgement — which is why the whole thing costs nothing
 * to run and nobody ever has to fill anything in:
 *
 *   1. WHO SPOKE LAST. Any portalproductions.me address counts as Portal, so
 *      Zeena's and Shilpa's messages are never mistaken for the client's. The
 *      first pass of this analysis got that wrong and inverted half the list.
 *   2. HOW LONG SINCE. Subtraction.
 *   3. UNANSWERED FOLLOW-UPS — messages Portal sent since their last reply.
 *      **This is the signal that finds a dying conversation**, not the date.
 *   4. BOUNCES AND AUTO-REPLIES. A delivery failure means nobody read it; an
 *      auto-reply means nobody answered it.
 *
 * The rows are a mirror, replaced wholesale by `sweep-dispatches` every half
 * hour, like `calendar_mirror`. What a person does — Done — lives in its own
 * table, so a refresh can never undo it.
 *
 * The studio's correspondence must never reach a client link. Every function
 * below is a SECURITY DEFINER RPC guarded by `is_portal_staff()`, the tables
 * have RLS on with no policies, and nothing here is imported by a client
 * route.
 */

export interface Conversation {
  threadId: string
  mailbox: string
  org: string
  person: string
  subject: string
  /** ISO timestamp of the last message on the thread, either direction. */
  lastAt: string
  /** True when Portal sent the last message — so we are the ones waiting. */
  lastFromPortal: boolean
  messageCount: number
  chased: number
  bounced: boolean
  autoReplyOnly: boolean
  threadUrl: string
  /** RFC 822 Message-ID of the first message. See `gmailLink`. */
  messageId: string
  done: boolean
  doneAt: string | null
}

/** Whole days between a timestamp and today, both taken at local midnight.
    Rounding a part-day up reads as a day older than it is — the figures on
    the first build of this screen were all one too high. */
export function daysSince(iso: string): number {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return 0
  const a = new Date(then.getFullYear(), then.getMonth(), then.getDate())
  const now = new Date()
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86_400_000))
}

/**
 * The one extra line a row is allowed, and only when there is one.
 *
 * THERE USED TO BE A "BROKEN" GROUP for bounces, auto-reply-only threads and
 * anything chased three times or more — a highlighted box at the top of the
 * screen. Tom killed it: *"The broken one is the most prominent but I don't
 * understand the point of this"*, then *"I don't think we even need the broken
 * bit"*. He was right. It bundled three unlike things, and with no bounces
 * open every row in the loudest box was a plain chase, repeating what the
 * Chasing list already said.
 *
 * **The facts survive as a line; only the category went.** That is the general
 * lesson and it keeps recurring on this screen: a fact can be shown without
 * being given a category, and a category costs every reader every day.
 *
 * Order matters — most serious first, one line only. A bounce outranks
 * everything because it is the only one of the three that means the other
 * person has not seen the email at all.
 */
export function rowNote(row: Conversation): string {
  if (row.bounced) return 'Delivery failed — they never received it'
  if (row.autoReplyOnly) return 'Only an auto-reply came back'
  if (row.chased > 0) {
    return `${row.chased} unanswered follow-${row.chased === 1 ? 'up' : 'ups'}`
  }
  return ''
}

/**
 * A link that opens in the READER's Gmail, not the sweeper's.
 *
 * A Gmail thread id belongs to one mailbox. The same conversation has a
 * different id in Tom's account and Zeena's, and the sweep keeps whichever
 * copy it met first — in practice Tom's, because he is swept first. Linking by
 * thread id therefore sent Zeena to an account she cannot open. Tom spotted
 * it before she ever did: *"when she clicks on a message it will take her to
 * my email account and not hers?"*
 *
 * Message-ID is written once by the sending server and is identical in every
 * mailbox that received the message, and Gmail can search for it directly. So
 * one stored value plus the viewer's own address produces a link that lands in
 * the right place for whoever is looking.
 *
 * If the thread is not in the viewer's mailbox at all — Tom was on it and
 * Zeena was not — the search finds nothing. That is honest, and far better
 * than opening somebody else's inbox.
 *
 * ## 6 October 2026 — the account was named in the wrong half of the URL
 *
 * Tom: *"when I am in Dispatches the email links do not go to my actual email
 * — they do for Zeena, but not for me."*
 *
 * The link was built as `mail.google.com/mail/u/?authuser=<address>#search/…`.
 * The segment after `/u/` is where Gmail reads the account, and it was empty;
 * `authuser` in the query string is not what Gmail routes on here. So every
 * link, for everybody, opened whichever Google account happens to be FIRST in
 * that browser. Zeena's worked by luck — her first account is her work one.
 * Anyone signed in to a second Google account first lands in the wrong inbox,
 * and the search finds nothing there, so it reads as a broken link.
 *
 * The address goes in the path instead, which is the form Gmail's own URLs
 * use. `0` is the fallback when we do not know who is looking: that is the
 * viewer's own default account, which is where the old link went anyway.
 */
export function gmailLink(row: Conversation, viewer: string): string {
  if (!row.messageId) return row.threadUrl
  const id = row.messageId.trim().replace(/^</, '').replace(/>$/, '')
  if (!id) return row.threadUrl
  const account = /^[^\s@/]+@[^\s@/]+$/.test(viewer.trim()) ? viewer.trim() : '0'
  return `https://mail.google.com/mail/u/${account}/#search/${encodeURIComponent(`rfc822msgid:${id}`)}`
}

/** Who is looking at the screen, for the link above. */
export async function currentStaffEmail(): Promise<string> {
  if (!supabase) return ''
  try {
    const { data } = await supabase.auth.getSession()
    return data.session?.user?.email ?? ''
  } catch {
    return ''
  }
}

function toConversation(raw: Record<string, unknown>): Conversation {
  return {
    threadId: String(raw.thread_id ?? ''),
    mailbox: String(raw.mailbox ?? ''),
    org: String(raw.org ?? ''),
    person: String(raw.person ?? ''),
    subject: String(raw.subject ?? ''),
    lastAt: String(raw.last_at ?? ''),
    lastFromPortal: Boolean(raw.last_from_portal),
    messageCount: Number(raw.message_count ?? 1),
    chased: Number(raw.chased ?? 0),
    bounced: Boolean(raw.bounced),
    autoReplyOnly: Boolean(raw.auto_reply_only),
    threadUrl: String(raw.thread_url ?? ''),
    messageId: String(raw.message_id ?? ''),
    done: Boolean(raw.done),
    doneAt: raw.done_at ? String(raw.done_at) : null,
  }
}

export async function fetchConversations(): Promise<Conversation[]> {
  if (!supabase) return []
  const { data, error } = await supabase.rpc('list_conversations')
  if (error) {
    console.warn(`[supabase] conversations load failed: ${error.message}`)
    return []
  }
  return ((data ?? []) as Record<string, unknown>[]).map(toConversation)
}

/**
 * Done, recorded against the thread AS IT STANDS.
 *
 * `lastAt` is stored with the mark, and the row is hidden only while the two
 * still match. A new message — from them or from us — makes the mark stale and
 * the conversation returns to the top. That comparison is the only reason a
 * one-press hide is safe: nothing can be lost by it, only postponed.
 */
export async function markConversationDone(threadId: string, lastAt: string): Promise<boolean> {
  if (!supabase) return false
  const { error } = await supabase.rpc('mark_conversation_done', {
    p_thread_id: threadId,
    p_last_at: lastAt,
  })
  if (error) {
    console.warn(`[supabase] mark done failed: ${error.message}`)
    return false
  }
  return true
}

export async function reopenConversation(threadId: string): Promise<boolean> {
  if (!supabase) return false
  const { error } = await supabase.rpc('reopen_conversation', { p_thread_id: threadId })
  if (error) {
    console.warn(`[supabase] reopen failed: ${error.message}`)
    return false
  }
  return true
}

/*
  THE BULK CLEAR'S EXCLUSION IS WIDER THAN ANYTHING THE SCREEN NOW SHOWS.

  `mark_conversations_done_before` and `count_conversations_before` refuse to
  touch anything bounced, auto-reply-only, OR chased three times or more. The
  screen no longer groups those, but the bulk clear still spares them, and it
  should: the screen's job is to be quiet, the bulk clear's job is to be
  careful, and a thread somebody has chased four times is not something to hide
  ninety at a time without looking. It was cleared once by accident, which is
  how the guard got written.

  THERE IS ALSO NO `ignoreConversationSender` HERE ANY MORE.

  A "Not a conversation" button sat on every row for about an hour, blacklisting
  the sender's domain for good. Tom cut it on sight: "This is just making
  everything convoluted — which is the ONE thing I want to avoid all the time
  (and worth remembering all the time as we build and tweak this OS)."

  The `conversation_ignores` table and the sweep's handling of it are intact, so
  a domain can still be silenced by adding a row. It is a rare job and it is
  done by asking, not by a control two people have to understand every day.
  Do not put it back on the row.
*/
